import { apiFetch } from "@/lib/api";
import type { ClientListMeta } from "@/features/clients/types";

/**
 * The Django API wraps every body in the standard envelope
 * (`APIJSONRenderer`): `{ success, message, data }`, plus `meta` for paginated
 * lists. `apiFetch` returns that envelope verbatim.
 */
interface Envelope<T> {
  data: T;
  meta?: ClientListMeta;
}

/** Stages a client moves through in the onboarding-approval pipeline. */
export type OnboardingStage =
  | "NEW_ARRIVAL"
  | "DOCS_REQUESTED"
  | "DOCS_COLLECTED"
  | "PENDING_APPROVAL"
  | "REJECTED"
  | "APPROVED"
  | "CLIENT_ACCEPTED"
  | "TEAM_ASSIGNED"
  | "ONBOARDED";

/** Actions the backend may expose from a given stage (already role/stage
 * filtered server-side — render whatever it returns). */
export type OnboardingAction =
  | "request_docs"
  | "docs_collected"
  | "submit_for_approval"
  | "approve"
  | "reject"
  | "client_accepted"
  | "client_reject"
  | "assign_team"
  | "assign_nsi_member"
  | "complete";

/** Human labels for stage badges. */
export const STAGE_LABELS: Record<OnboardingStage, string> = {
  NEW_ARRIVAL: "New arrival",
  DOCS_REQUESTED: "Docs requested",
  DOCS_COLLECTED: "Docs collected",
  PENDING_APPROVAL: "Requested PI",
  REJECTED: "Rejected",
  APPROVED: "Approved",
  CLIENT_ACCEPTED: "Client accepted",
  TEAM_ASSIGNED: "Team assigned",
  ONBOARDED: "Onboarded",
};

/** Human labels for the action buttons. */
export const ACTION_LABELS: Record<OnboardingAction, string> = {
  request_docs: "Request docs",
  docs_collected: "Docs collected",
  submit_for_approval: "Prepare & send PI",
  approve: "Approve",
  reject: "Reject",
  client_accepted: "Mark client accepted",
  client_reject: "Record client rejection",
  assign_team: "Assign team",
  assign_nsi_member: "Assign NSI member",
  complete: "Complete onboarding",
};

/** Ordered happy-path stages for the progress indicator. REJECTED is a
 * side-state (a rejected client sits back at the CD lane) so it's excluded from
 * the linear track and surfaced separately. */
export const PROGRESS_STAGES: OnboardingStage[] = [
  "NEW_ARRIVAL",
  "DOCS_REQUESTED",
  "DOCS_COLLECTED",
  "PENDING_APPROVAL",
  "APPROVED",
  "CLIENT_ACCEPTED",
  "TEAM_ASSIGNED",
  "ONBOARDED",
];

/**
 * A new-arrivals row: the clients-list summary shape plus the onboarding
 * fields. Kept intentionally lean — the queue only reads these — rather than
 * re-deriving the full editable client profile.
 */
export interface NewArrival {
  id: string;
  name: string;
  type: string;
  status: string;
  onboardingStage: OnboardingStage;
  onboardingRejectionReason: string;
  /** Owning firm (for the GA/SA cross-org view). */
  firmId: string | null;
  firmName: string | null;
}

interface ApiNewArrival {
  id: string;
  name: string;
  type: string;
  status: string;
  onboarding_stage: OnboardingStage;
  onboarding_rejection_reason?: string | null;
  firm?: { id: string; name: string } | null;
  firm_id?: string | null;
  firm_name?: string | null;
}

function toNewArrival(api: ApiNewArrival): NewArrival {
  // The firm can arrive either nested (`firm: {id, name}`) or flat
  // (`firm_id`/`firm_name`); normalize both.
  const firmId = api.firm?.id ?? api.firm_id ?? null;
  const firmName = api.firm?.name ?? api.firm_name ?? null;
  return {
    id: String(api.id),
    name: api.name,
    type: api.type,
    status: api.status,
    onboardingStage: api.onboarding_stage,
    onboardingRejectionReason: api.onboarding_rejection_reason ?? "",
    firmId: firmId != null ? String(firmId) : null,
    firmName,
  };
}

export interface NewArrivalsResult {
  arrivals: NewArrival[];
  meta: ClientListMeta;
}

export interface ListNewArrivalsParams {
  stage?: OnboardingStage;
  page?: number;
  pageSize?: number;
  /** Scope the queue to one firm — a GA/SA running a specific BP's onboarding. */
  firm?: string;
}

/** GET /clients/new-arrivals/ — paginated; GA/SA see every org, org users see
 * only their own firm's. Pass `firm` to scope to one accessible firm. */
export async function listNewArrivals(
  params: ListNewArrivalsParams = {},
): Promise<NewArrivalsResult> {
  const q = new URLSearchParams();
  if (params.stage) q.set("stage", params.stage);
  if (params.page) q.set("page", String(params.page));
  if (params.pageSize) q.set("page_size", String(params.pageSize));
  if (params.firm) q.set("firm", params.firm);
  const query = q.toString() ? `?${q.toString()}` : "";
  const body = await apiFetch<Envelope<ApiNewArrival[]>>(`/clients/new-arrivals/${query}`);
  return {
    arrivals: body.data.map(toNewArrival),
    meta: body.meta ?? { count: body.data.length, next: null, previous: null },
  };
}

/** One entry in the onboarding audit trail. */
export interface OnboardingEvent {
  id: string;
  action: string;
  fromStage: OnboardingStage | null;
  toStage: OnboardingStage | null;
  actor: string | null;
  actorName: string;
  note: string;
  createdAt: string;
}

/** GET /clients/:id/onboarding/ result — `availableActions` is already filtered
 * to what the calling user may do from the current stage. */
export interface OnboardingState {
  stage: OnboardingStage;
  rejectionReason: string;
  availableActions: OnboardingAction[];
  /** The client's OWN firm members — the assignable team for `assign_team`
   * (resolved server-side so a GA/SA managing a cross-org arrival gets the
   * client's firm, not their own). */
  assignableMembers: { id: string; name: string }[];
  /** True once the BP handed this client to NSI Capitals. */
  handedToNsi: boolean;
  /** NSI members currently assigned (preselects the assign-NSI picker). */
  nsiMemberIds: string[];
  /** House Country Director option(s) the BP picks to hand off to NSI (only
   * populated for a BP client). */
  nsiCountryDirector: { id: string; name: string }[];
  /** House team members the house Country Director assigns from (only populated
   * when the viewer may run `assign_nsi_member`). */
  nsiAssignableMembers: { id: string; name: string }[];
  /** The current Proforma Invoice PDF (a Document) the CD uploaded, or null. */
  proforma: { id: string; name: string } | null;
  events: OnboardingEvent[];
}

interface ApiOnboardingEvent {
  id: string | number;
  action: string;
  from_stage: OnboardingStage | null;
  to_stage: OnboardingStage | null;
  actor: string | number | null;
  actor_name: string;
  note?: string | null;
  created_at: string;
}

interface ApiOnboardingState {
  stage: OnboardingStage;
  rejection_reason?: string | null;
  available_actions: OnboardingAction[];
  assignable_members?: { id: string | number; name: string }[];
  handed_to_nsi?: boolean;
  nsi_member_ids?: (string | number)[];
  nsi_country_director?: { id: string | number; name: string }[];
  nsi_assignable_members?: { id: string | number; name: string }[];
  proforma?: { id: string | number; name: string } | null;
  events: ApiOnboardingEvent[];
}

/** Normalize an id/name option list from the API. */
function toOptions(
  list?: { id: string | number; name: string }[],
): { id: string; name: string }[] {
  return (list ?? []).map((m) => ({ id: String(m.id), name: m.name }));
}

function toEvent(api: ApiOnboardingEvent): OnboardingEvent {
  return {
    id: String(api.id),
    action: api.action,
    fromStage: api.from_stage,
    toStage: api.to_stage,
    actor: api.actor != null ? String(api.actor) : null,
    actorName: api.actor_name,
    note: api.note ?? "",
    createdAt: api.created_at,
  };
}

/** GET /clients/:id/onboarding/ — current stage, permitted actions, timeline. */
export async function getOnboarding(clientId: string): Promise<OnboardingState> {
  const body = await apiFetch<Envelope<ApiOnboardingState>>(`/clients/${clientId}/onboarding/`);
  return {
    stage: body.data.stage,
    rejectionReason: body.data.rejection_reason ?? "",
    availableActions: body.data.available_actions ?? [],
    assignableMembers: toOptions(body.data.assignable_members),
    handedToNsi: body.data.handed_to_nsi ?? false,
    nsiMemberIds: (body.data.nsi_member_ids ?? []).map(String),
    nsiCountryDirector: toOptions(body.data.nsi_country_director),
    nsiAssignableMembers: toOptions(body.data.nsi_assignable_members),
    proforma: body.data.proforma
      ? { id: String(body.data.proforma.id), name: body.data.proforma.name }
      : null,
    events: (body.data.events ?? []).map(toEvent),
  };
}

export interface RequiredDocInput {
  name: string;
  notes?: string;
}

export interface TransitionBody {
  action: OnboardingAction;
  /** Required for `reject` (the rework reason); optional elsewhere. */
  note?: string;
  /** Only meaningful for `assign_team`. */
  assigned_member_ids?: number[];
  /** `assign_team` only — hand the client to NSI Capitals (the BP picked the
   * NSI Country Director option). */
  hand_off_to_nsi?: boolean;
  /** `assign_nsi_member` only — the house team member(s) to assign. */
  nsi_member_ids?: number[];
  /** Only meaningful for `request_docs` — the checklist to seed as the client's
   * required documents (from the confirmed "Request documents" popup). */
  required_documents?: RequiredDocInput[];
}

/** GET /clients/:id/required-documents/defaults/ — the firm's per-client-type
 * default checklist, used to pre-fill the "Request documents" popup. */
export async function fetchRequiredDocDefaults(
  clientId: string,
): Promise<RequiredDocInput[]> {
  const body = await apiFetch<Envelope<{ documents: RequiredDocInput[] }>>(
    `/clients/${clientId}/required-documents/defaults/`,
  );
  return (body.data.documents ?? []).map((d) => ({ name: d.name, notes: d.notes ?? "" }));
}

/**
 * POST /clients/:id/onboarding/transition/ — perform one pipeline action. The
 * backend 400s on an invalid action/stage and 403 when the user may not perform
 * it; both surface as `ApiError` for the caller to toast. Returns the updated
 * client (callers refetch the onboarding state rather than reshaping it).
 */
export async function transitionOnboarding(
  clientId: string,
  body: TransitionBody,
): Promise<void> {
  await apiFetch<unknown>(`/clients/${clientId}/onboarding/transition/`, {
    method: "POST",
    json: body,
  });
}

/**
 * POST /clients/:id/onboarding/transition/ — `submit_for_approval` carrying the
 * Country Director's Proforma Invoice PDF (multipart). Same endpoint as
 * `transitionOnboarding`; separate because it sends a file.
 */
export async function submitProformaForApproval(
  clientId: string,
  file: File,
): Promise<void> {
  const form = new FormData();
  form.append("action", "submit_for_approval");
  form.append("proforma", file);
  await apiFetch<unknown>(`/clients/${clientId}/onboarding/transition/`, {
    method: "POST",
    form,
  });
}
